/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends customElements.get('product-form') {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.buyButtons = this.section?.querySelector('.quantity-submit-row');
        if (!this.buyButtons) return;

        this.mainProductForm = this.section.querySelector('product-form');
        this.mainProductSubmitForm = this.mainProductForm?.getSubmitForm();
        this.submitForm = this.getSubmitForm();
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.variantTitle = this.querySelector('.sticky-atc__details__variant__title');
        this.variantTitle?.toggleAttribute('hidden', !this.section.querySelector('variant-picker'));
        this.button = this.querySelector('.add-to-cart');

        this.throttledOnScroll = throttle(this.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll, { passive: true });

        this.updateImageHandler = this.updateImage.bind(this);
        this.section.addEventListener('on:media-gallery:change', this.updateImageHandler);

        this.updateImage();
        this.updateAddToCartButton();

        // Phones: sizes sit in the bar beside Add to cart. A size must be tapped before adding,
        // so nobody adds the preselected size by mistake.
        this.pillClickHandler = this.handlePillClick.bind(this);
        this.addEventListener('click', this.pillClickHandler);
        this.sizePickHandler = this.handleSizePick.bind(this);
        this.section.addEventListener('change', this.sizePickHandler, true);
        this.section.addEventListener('click', this.sizePickHandler, true);
        this.throttledOnResize = throttle(() => {
          this.updateChoiceState();
          this.handleScroll();
        });
        window.addEventListener('resize', this.throttledOnResize, { passive: true });

        this.updateChoiceState();
        this.handleScroll();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
        window.removeEventListener('resize', this.throttledOnResize);
        this.removeEventListener('click', this.pillClickHandler);
        clearTimeout(this.promptTimer);
        this.section?.removeEventListener('on:media-gallery:change', this.updateImageHandler);
        this.section?.removeEventListener('change', this.sizePickHandler, true);
        this.section?.removeEventListener('click', this.sizePickHandler, true);
      }

      /**
       * Handles submission of the product form.
       * @param {object} evt - Event object.
       */
      async handleSubmit(evt) {
        evt.preventDefault();
        if (!this.section || !this.mainProductForm || !this.mainProductSubmitForm || !this.submitForm) return;
        if (this.needsSizeChoice()) {
          if (this.getPills().length) {
            this.promptSize();
          } else {
            this.revealSizePicker();
          }
          return;
        }
        if (this.button?.dataset.wjSoldOut === 'true') {
          const target = this.section.querySelector('#product-info');
          if (target) theme.scrollToRevealElement(target);
          return;
        }
        if (this.section?.dataset.wjVariantLoading === 'true' || this.section?.dataset.wjVariantError === 'true') {
          if (this.section?.dataset.wjVariantError === 'true') {
            const error = this.section.querySelector('.js-form-error');
            if (error) {
              error.textContent = theme.strings.variantUpdateError;
              error.hidden = false;
            }
          }
          return;
        }

        const variantId = this.section?.querySelector('.js-product-form input[name="id"]')?.value;
        if (!variantId) {
          const target = this.section.querySelector('#product-info');
          if (target && target.offsetParent) {
            evt.preventDefault();
            theme.scrollToRevealElement(target);
          }
          return;
        }

        const stickyVariantInput = this.submitForm.querySelector('input[name="id"]');
        if (stickyVariantInput) stickyVariantInput.value = variantId;

        // Validate main form
        const customFormValid = this.mainProductForm.validate();
        if (!customFormValid || !this.mainProductSubmitForm.reportValidity()) {
          evt.preventDefault();
          if (!customFormValid) {
            theme.scrollToRevealElement(this.mainProductForm);
          } else {
            const input = Array.from(this.mainProductSubmitForm.elements).find(
              (el) => !el.checkValidity()
            );
            setTimeout(() => theme.scrollToRevealElement(input), 100);
          }
          return;
        }

        // Clear copied data
        this.submitForm.querySelectorAll('[data-copied]').forEach((el) => el.remove());

        // Copy data
        const formData = new FormData(this.mainProductSubmitForm);
        for (const p of formData) {
          if (!this.submitForm.querySelector(`[name="${p[0]}"]`)) {
            const input = document.createElement('input');
            input.name = p[0];
            input.value = p[1];
            input.hidden = true;
            input.setAttribute('data-copied', '');
            this.submitForm.append(input);
          }
        }
        super.handleSubmit(evt);
      }

      /**
       * Determine visibility after scroll.
       */
      handleScroll() {
        if (!this.buyButtons) return;
        const topOffset = document.querySelector('.pageheader--sticky') ? (document.querySelector('.section-header')?.offsetHeight || 0) : 0;
        const rect = this.buyButtons.getBoundingClientRect();
        // Phones: show the bar from the start, hiding it only while the main buy buttons are on screen
        const out = window.innerWidth < 768
          ? rect.top < window.innerHeight && rect.bottom > topOffset
          : rect.bottom > topOffset;
        this.classList.toggle('sticky-atc--out', out);

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Returns the size option selector, if the product has more than one size.
       * @returns {?Element}
       */
      getSizeSelector() {
        const selector = Array.from(this.section?.querySelectorAll('variant-picker .option-selector') || [])
          .find((el) => /size|talla|tama[nñ]o/i.test(el.dataset.option || ''));
        if (!selector || selector.querySelectorAll('.js-option').length < 2) return null;
        return selector;
      }

      /**
       * Returns the size buttons in the bar.
       * @returns {Array}
       */
      getPills() {
        return Array.from(this.querySelectorAll('.sticky-atc__size'));
      }

      /**
       * True when the shopper has picked a size, only one size is in stock and it is selected,
       * or every size is sold out (nothing left to pick).
       * @returns {boolean}
       */
      isSizeChosen() {
        if (this.section?.dataset.wjSizeChosen === 'true') return true;
        const pills = this.getPills();
        const open = pills.filter((pill) => !pill.disabled);
        if (pills.length && !open.length) return true;
        return open.length === 1 && open[0].classList.contains('is-current');
      }

      /**
       * True on phones until the shopper has tapped a size themselves.
       * @returns {boolean}
       */
      needsSizeChoice() {
        if (window.innerWidth >= 768) return false;
        if (this.isSizeChosen()) return false;
        return !!this.getSizeSelector();
      }

      /**
       * Remembers a size the shopper picked by hand.
       * @param {object} evt - Event object.
       */
      handleSizePick(evt) {
        if (!evt.isTrusted) return;
        const selector = evt.target.closest?.('variant-picker .option-selector');
        if (!selector || !/size|talla|tama[nñ]o/i.test(selector.dataset.option || '')) return;
        if (evt.type === 'click' && !evt.target.closest('.opt-label, .opt-btn, .js-option')) return;
        this.section.dataset.wjSizeChosen = 'true';
        this.updateChoiceState();
      }

      /**
       * Selects a size from the bar by selecting the same size in the main picker.
       * @param {object} evt - Event object.
       */
      handlePillClick(evt) {
        const pill = evt.target.closest?.('.sticky-atc__size');
        if (!pill || pill.disabled || !this.section) return;
        if (this.section.dataset.wjVariantLoading === 'true') return;
        const input = this.section.querySelector(
          `variant-picker .js-option[data-value-id="${CSS.escape(pill.dataset.valueId || '')}"]`
        );
        if (!input) return;

        this.section.dataset.wjSizeChosen = 'true';
        this.classList.remove('sticky-atc--prompt');
        this.getPills().forEach((el) => el.classList.toggle('is-current', el === pill));
        this.updateChoiceState();
        if (input.checked) return;

        // The bar is re-rendered for the new size once the main picker has fetched it
        input.checked = true;
        input.dispatchEvent(new CustomEvent('change', { bubbles: true, cancelable: false, detail: { noFocus: true } }));
      }

      /**
       * Shows the sizes are needed, without moving the page.
       */
      promptSize() {
        clearTimeout(this.promptTimer);
        this.classList.remove('sticky-atc--prompt');
        // Restart the animation
        void this.offsetWidth;
        this.classList.add('sticky-atc--prompt');
        this.promptTimer = setTimeout(() => this.classList.remove('sticky-atc--prompt'), 2800);
      }

      /**
       * Keeps the size buttons and the Add to cart label in step with the chosen size.
       */
      updateChoiceState() {
        const chosen = this.isSizeChosen();
        this.classList.toggle('sticky-atc--choose', this.needsSizeChoice());
        this.classList.toggle('sticky-atc--size-chosen', chosen);
        this.getPills().forEach((pill) => {
          pill.setAttribute('aria-pressed', chosen && pill.classList.contains('is-current') ? 'true' : 'false');
        });
        if (!this.button || this.button.tagName !== 'BUTTON' || this.button.dataset.wjSoldOut !== 'true') return;
        this.button.textContent = chosen || window.innerWidth >= 768
          ? (this.dataset.notifyText || 'Notify me')
          : this.button.dataset.addToCartText;
      }

      /**
       * Scrolls to the size buttons and briefly highlights them.
       */
      revealSizePicker() {
        const selector = this.getSizeSelector();
        if (!selector) return;
        selector.scrollIntoView({ behavior: 'smooth', block: 'center' });
        selector.classList.remove('wj-size-prompt');
        requestAnimationFrame(() => selector.classList.add('wj-size-prompt'));
        setTimeout(() => selector.classList.remove('wj-size-prompt'), 1600);
      }

      /**
       * Updates the image.
       */
      updateImage() {
        if (!this.section || !this.imageContainer) return;
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the add to cart button text.
       */
      updateAddToCartButton() {
        // If there is no variant selected, show unavailable-text
        const variantId = this.section?.querySelector('.js-product-form input[name="id"]')?.value;
        if (!variantId) {
          if (!this.button) return;
          this.button.innerHTML = this.button.dataset.unavailableText;
        }
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}

